import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, LoaderCircle, RotateCcw, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AccountRepairBanner } from '@/_shared/components/AccountRepair/AccountRepairBanner';
import { RelinkNotice } from '@/_shared/components/AccountRepair/RelinkNotice';
import { Button } from '@/_shared/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/_shared/components/ui/empty';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/_shared/components/ui/sheet';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useIsMobile } from '@/_shared/hooks/use-mobile';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { useStarred } from '@/_shared/hooks/useStarred';
import { ANALYTICS_EVENTS, trackClientEvent } from '@/_shared/lib/analytics';
import { isOrpcNotFoundError } from '@/_shared/lib/orpc-errors';
import { usePreviewPanelSlide } from '@/chat/hooks/usePreviewPanelSlide';
import { buildPanelMentions, MENTION_TRIGGER, type PanelMention } from '@/chat/lib/panel-mentions';

import {
  DASHBOARD_ASSISTANT_DESKTOP_ID,
  DASHBOARD_ASSISTANT_MOBILE_ID,
  DashboardAssistantSlideShell,
} from '../components/DashboardAssistantSlideShell';
import { DashboardChatSidebar } from '../components/DashboardChatSidebar';
import { DashboardHeader } from '../components/DashboardHeader';
import { DashboardParamBar } from '../components/DashboardParamBar';
import { PanelWithData } from '../components/PanelWithData';
import { useDashboardChat } from '../hooks/useDashboardChat';
import { useDeferredParamDefaults } from '../hooks/useDeferredParamDefaults';
import { useDynamicParamOptions } from '../hooks/useDynamicParamOptions';
import { computeDefaults, searchParamsToValues, valuesToSearchParams } from '../lib/dashboard-params';

function panelCountText(count: number): string {
  return count === 1 ? 'Its panel' : `Its ${String(count)} panels`;
}

export function Dashboard(): React.ReactElement {
  const { dashSlug: slug } = useParams<{ dashSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { wp, isAdmin } = useWorkspace();
  const { user } = useAuth();
  const orpc = useOrpc();
  const { isStarred, toggleStar, isToggling } = useStarred('dashboard');

  // Fetch dashboard
  const {
    data: dashboardData,
    isLoading,
    error,
  } = useQuery(
    orpc.dashboard.get.queryOptions({
      input: slug ? { slug } : { slug: '' },
    }),
  );
  const dashboard = dashboardData?.dashboard ?? null;
  // ACT2-1286: the needs-repair banner and relink notice. Flag off: the server sends the off view and
  // neither renders.
  const repair = dashboardData?.accountRepair;
  usePageTitle(dashboard?.title);

  // Resolve dynamic parameter options (params with sql field)
  const {
    parameters: resolvedParams,
    isLoading: paramsLoading,
    loadingParamNames,
  } = useDynamicParamOptions(dashboard?.id, dashboard?.spec.parameters ?? [], orpc);

  // Merge defaults with URL search params
  const paramValues = useMemo((): Record<string, string | string[]> => {
    if (!dashboard) return {};
    const defaults = computeDefaults(resolvedParams);
    const fromUrl = searchParamsToValues(searchParams, resolvedParams);
    return { ...defaults, ...fromUrl };
  }, [dashboard, resolvedParams, searchParams]);

  // Deferred defaults: seed missing params AND auto-correct invalid option values once loaded (AI-1186).
  useDeferredParamDefaults(!paramsLoading && !!dashboard, resolvedParams, searchParams, setSearchParams);

  // Sync param changes to URL
  const handleParamChange = useCallback(
    (newValues: Record<string, string | string[]>) => {
      setSearchParams(valuesToSearchParams(newValues), { replace: true });
    },
    [setSearchParams],
  );

  // Plaintext share token, fresh from a mint (publish or regenerate). The
  // backend never returns it on dashboard.get — it lives only in component
  // state for the popover, and disappears as soon as the user navigates away.
  const [plaintextToken, setPlaintextToken] = useState<string | null>(null);

  const invalidateDashboardQueries = useCallback((): void => {
    if (slug) {
      void queryClient.invalidateQueries({
        queryKey: orpc.dashboard.get.queryOptions({ input: { slug } }).queryKey,
      });
    }
    void queryClient.invalidateQueries({
      queryKey: orpc.dashboard.list.queryOptions({ input: {} }).queryKey,
    });
  }, [orpc, queryClient, slug]);

  const privacyMutation = useMutation(
    orpc.dashboard.updatePrivacy.mutationOptions({
      onSuccess: (result) => {
        // updatePrivacy returns { ok: true, token } when going public,
        // { ok: true } when going private. Capture the fresh token; clear
        // it on unpublish so the stale value doesn't bleed across renders.
        const token = 'token' in result ? result.token : null;
        setPlaintextToken(token ?? null);
        invalidateDashboardQueries();
        toast.success(token ? 'Published — copy the share link to share' : 'Unpublished — link is now inactive');
      },
      onError: (err: { message?: string }) => {
        // A silent failure here is a privacy regression: e.g. a 429 on
        // unpublish would leave the user thinking the dashboard is now
        // private when it is still public.
        toast.error(err.message ?? 'Failed to update visibility');
      },
    }),
  );

  const regenerateMutation = useMutation(
    orpc.dashboard.regenerateShareToken.mutationOptions({
      onSuccess: (result) => {
        setPlaintextToken(result.token);
        invalidateDashboardQueries();
        toast.success('New share link minted');
      },
      onError: (err: { message?: string }) => {
        // Critical: silent failure here means the user shares a "rotated"
        // link that is actually the still-leaked old token.
        toast.error(err.message ?? 'Failed to rotate share link');
      },
    }),
  );

  const handlePublish = useCallback((): void => {
    if (!dashboard) return;
    privacyMutation.mutate({ dashboardId: dashboard.id, isPublic: true });
  }, [dashboard, privacyMutation]);

  const handleUnpublish = useCallback((): void => {
    if (!dashboard) return;
    privacyMutation.mutate({ dashboardId: dashboard.id, isPublic: false });
  }, [dashboard, privacyMutation]);

  const handleRegenerate = useCallback((): void => {
    if (!dashboard) return;
    // Confirm before rotation: the existing link dies the moment we mint a
    // new one, and the backend has no way to undo it.
    const ok = window.confirm('Anyone holding the current link will lose access. Continue?');
    if (!ok) return;
    regenerateMutation.mutate({ dashboardId: dashboard.id });
  }, [dashboard, regenerateMutation]);

  const deleteMutation = useMutation(
    orpc.dashboard.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.dashboard.list.queryOptions({ input: {} }).queryKey,
        });
        toast.success('Dashboard deleted');
        void navigate(wp('/dashboards'));
      },
      onError: (err: { message?: string }) => {
        toast.error(err.message ?? 'Failed to delete dashboard');
      },
    }),
  );

  // A confirmed repair refreshes the dashboard, the list's status and every panel's query.
  const repairInvalidateKeys = useMemo(() => [orpc.dashboard.key()], [orpc]);
  // Dismissing the notice changes only the dashboard read, never an account or a query: refetching the
  // whole namespace would rerun every panel's warehouse SQL for nothing.
  const dismissInvalidateKeys = useMemo(() => [orpc.dashboard.get.key()], [orpc]);

  const handleDelete = useCallback(() => {
    if (!dashboard) return;
    deleteMutation.mutate({ id: dashboard.id });
  }, [dashboard, deleteMutation]);

  const handleToggleStar = useCallback((): void => {
    if (!dashboard) return;
    toggleStar(dashboard.id, !isStarred(dashboard.id));
  }, [dashboard, isStarred, toggleStar]);

  // Sort panels by order

  const sortedPanels = useMemo(() => {
    if (!dashboard) return [];
    return [...dashboard.spec.panels].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [dashboard]);

  // Dashboard chat sidebar
  const chat = useDashboardChat(dashboard, paramValues);
  const { openAndSend, open: openChat, toggle: toggleChat, close: closeChat, isOpen: chatIsOpen } = chat;
  /** The mention the ✨ queued, and a nonce so the same panel can be sent twice. */
  const [pendingMention, setPendingMention] = useState<{ mention: PanelMention; nonce: number } | null>(null);
  const panelMentions = useMemo(() => buildPanelMentions(dashboard?.spec.panels ?? []), [dashboard?.spec.panels]);
  const isMobile = useIsMobile();
  // Latch the desktop split after first open so close can animate without remounting the shell.
  // Safe while "closed": useDashboardChat lives at page level either way, and SSE is turn-scoped
  // (streamUrl is null unless an active turn is running) — keeping the shell mounted does not hold
  // a live chat subscription open.
  const [splitEverOpened, setSplitEverOpened] = useState(false);
  const assistantSlide = usePreviewPanelSlide(chatIsOpen && !isMobile);
  const useSplitLayout = splitEverOpened && !isMobile;
  const chatSidebarId = isMobile ? DASHBOARD_ASSISTANT_MOBILE_ID : DASHBOARD_ASSISTANT_DESKTOP_ID;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- latch split layout once assistant opens so close animation keeps split chrome
    if (chatIsOpen && !isMobile) setSplitEverOpened(true);
  }, [chatIsOpen, isMobile]);

  // Panel → sidebar assistant actions

  // ✨ writes the panel into the sentence and lets the user say what they want — including on a panel
  // that errored, where "why is this failing?" is not the same ask as "fix it".
  const handleAskAssistant = useCallback(
    (panel: { id: string; title: string }) => {
      const mention = panelMentions.find((m) => m.id === panel.id);
      if (!mention) return;
      openChat();
      // Queued, not pushed. The sidebar mounts on this state change and the editor is built after
      // that, so an immediate write would land before either existed — which is why the first ✨ of
      // a session used to do nothing until the sidebar had been opened once.
      setPendingMention((prev) => ({ mention, nonce: (prev?.nonce ?? 0) + 1 }));
      if (dashboard) {
        trackClientEvent(ANALYTICS_EVENTS.dashboardPanelReferenced, { dashboardId: dashboard.id, source: 'button' });
      }
    },
    [openChat, panelMentions, dashboard],
  );

  // The error CTA is the one place a canned prompt is right, so it stays a single click. It names the
  // panel the same way the ✨ does — a mention in the sentence, and the id beside it — so the bubble
  // reads like every other message, and a title shared by two panels still points at one of them.
  const handleFixError = useCallback(
    (panel: { id: string; title: string }, error: string) => {
      const mention = panelMentions.find((m) => m.id === panel.id);
      if (!mention) return;
      openAndSend(`${MENTION_TRIGGER}${mention.label} has an error:\n\n${error}\n\nPlease investigate and fix it.`, [
        panel.id,
      ]);
    },
    [openAndSend, panelMentions],
  );

  const handleMentionPanel = useCallback(() => {
    if (dashboard) {
      trackClientEvent(ANALYTICS_EVENTS.dashboardPanelReferenced, { dashboardId: dashboard.id, source: 'mention' });
    }
  }, [dashboard]);

  // Keyboard shortcuts: Cmd+Shift+K to toggle sidebar, Escape to close
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggleChat();
        return;
      }
      if (e.key === 'Escape' && chatIsOpen) {
        e.preventDefault();
        closeChat();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleChat, closeChat, chatIsOpen]);

  const dashboardMain = dashboard ? (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <DashboardHeader
        dashboard={dashboard}
        onToggleChat={chat.toggle}
        chatOpen={chat.isOpen}
        chatSidebarId={chatSidebarId}
        isAdmin={isAdmin || dashboard.createdBy === user?.id}
        onDelete={handleDelete}
        deletePending={deleteMutation.isPending}
        plaintextToken={plaintextToken}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
        onRegenerate={handleRegenerate}
        regeneratePending={regenerateMutation.isPending}
        privacyPending={privacyMutation.isPending}
        isStarred={isStarred(dashboard.id)}
        onToggleStar={handleToggleStar}
        starPending={isToggling(dashboard.id)}
        repairState={repair?.repairEnabled ? repair.state : 'ok'}
      />
      {repair?.repairEnabled && (repair.state !== 'ok' || repair.notice) && (
        <div className="flex flex-col gap-2 px-6 pt-3">
          <AccountRepairBanner
            itemRef={{ itemType: 'dashboard', id: dashboard.id }}
            itemNoun="dashboard"
            view={repair}
            invalidateKeys={repairInvalidateKeys}
            detail={
              sortedPanels.length === 0
                ? undefined
                : `${panelCountText(sortedPanels.length)} can't run until ${
                    !repair.canRepair
                      ? 'it is repaired'
                      : repair.state === 'unassigned'
                        ? 'you pick an account'
                        : 'you pick another account'
                  }.`
            }
          />
          <RelinkNotice
            notice={repair.state === 'ok' ? repair.notice : null}
            itemRef={{ itemType: 'dashboard', id: dashboard.id }}
            invalidateKeys={dismissInvalidateKeys}
            repairEnabled={repair.repairEnabled}
          />
        </div>
      )}
      <DashboardParamBar
        parameters={resolvedParams}
        values={paramValues}
        onChange={handleParamChange}
        loadingParamNames={loadingParamNames}
      />
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 px-6 py-6 lg:grid-cols-2">
          {sortedPanels.map((panel) => (
            <PanelWithData
              key={panel.id}
              source={{ mode: 'saved', dashboardId: dashboard.id }}
              panel={panel}
              params={paramValues}
              paramsLoading={paramsLoading}
              onAskAssistant={handleAskAssistant}
              onFixError={handleFixError}
              keepDataOnRefetch
            />
          ))}
        </div>
      </div>
    </div>
  ) : null;

  const assistantPanel = dashboard ? (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
        <span className="text-sm font-semibold text-foreground">Dashboard Assistant</span>
        <div className="flex items-center gap-1">
          {chat.messages.length > 0 && (
            <Button variant="tertiary" onClick={chat.resetChat} aria-label="Reset conversation" Icon={<RotateCcw />} />
          )}
          <Button variant="tertiary" onClick={chat.close} aria-label="Close sidebar" Icon={<X />} />
        </div>
      </div>
      <DashboardChatSidebar
        dashboard={dashboard}
        paramValues={paramValues}
        sessionId={chat.sessionId}
        messages={chat.messages}
        agentActive={chat.agentActive}
        canStop={chat.canStop}
        stopping={chat.stopping}
        stopStatusError={chat.stopStatusError}
        stopStalled={chat.stopStalled}
        onRetryStopStatus={chat.retryStopStatus}
        disabled={!chat.sessionReady}
        budgetExhausted={chat.budgetExhausted}
        onSend={chat.sendMessage}
        onReset={chat.resetChat}
        onClose={chat.close}
        onStop={chat.stopTurn}
        prefillValue={chat.prefillValue}
        prefillNonce={chat.prefillNonce}
        panelMentions={panelMentions}
        onPanelMentioned={handleMentionPanel}
        pendingMention={pendingMention?.mention}
        pendingMentionNonce={pendingMention?.nonce}
      />
    </div>
  ) : null;

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      {isLoading && (
        <div className="flex flex-1 items-center justify-center">
          <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <div className="flex flex-1 items-center justify-center p-6">
          <Empty className="max-w-md border-0">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <CircleAlert aria-hidden />
              </EmptyMedia>
              <EmptyTitle>{isOrpcNotFoundError(error) ? 'Dashboard not found' : 'Could not load dashboard'}</EmptyTitle>
              <EmptyDescription>
                {isOrpcNotFoundError(error)
                  ? 'This dashboard is no longer in the workspace, or the link may be incorrect.'
                  : 'Something went wrong while loading this dashboard. Try again in a moment.'}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="secondary" asChild>
                <Link to={wp('/dashboards')}>Back to dashboards</Link>
              </Button>
            </EmptyContent>
          </Empty>
        </div>
      )}

      {dashboard && (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <Sheet
            open={chatIsOpen && isMobile}
            onOpenChange={(open) => {
              if (!open) closeChat();
            }}
          >
            <SheetContent
              side="right"
              showCloseButton={false}
              className="flex w-full max-w-full flex-col gap-0 p-0 sm:max-w-full md:hidden"
            >
              <SheetTitle className="sr-only">Dashboard Assistant</SheetTitle>
              <SheetDescription className="sr-only">
                Ask questions about this dashboard and get AI-powered insights.
              </SheetDescription>
              <div id={DASHBOARD_ASSISTANT_MOBILE_ID} className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {assistantPanel}
              </div>
            </SheetContent>
          </Sheet>

          {useSplitLayout ? (
            <DashboardAssistantSlideShell shown={assistantSlide.shown} onClose={closeChat} main={dashboardMain}>
              {assistantPanel}
            </DashboardAssistantSlideShell>
          ) : (
            dashboardMain
          )}
        </div>
      )}
    </div>
  );
}
